<template>
  <div>
    <b-modal
      :visible="visible"
      size="xl"
      centered
      no-close-on-backdrop
      modal-class="wl-enroll-patient-modal"
      body-class="p-0"
      @change="val => $emit('update:visible', val)"
      @hidden="onHidden">
      <template #modal-header="{ close }">
        <div class="w-100">
          <div class="d-flex align-items-center mb-3">
            <i class="fas fa-user-plus text-primary mr-3 fa-lg"></i>
            <div class="flex-grow-1">
              <h5 class="mb-0 font-weight-bolder">{{ modalTitle }}</h5>
              <small class="text-muted">{{ modalSubtitle }}</small>
            </div>
            <button type="button" class="close" aria-label="Close" @click="close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div v-if="!isEdit" class="wl-wizard-steps d-flex">
            <div
              v-for="(step, index) in steps"
              :key="step.key"
              class="wl-wizard-step flex-fill text-center"
              :class="{ active: currentStep === index + 1, done: currentStep > index + 1 }">
              <div class="wl-wizard-step-circle mx-auto mb-1">
                <i v-if="currentStep > index + 1" class="fas fa-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <small class="font-weight-bold">{{ step.label }}</small>
            </div>
          </div>
        </div>
      </template>

      <!-- Step 1: Clinic patient (search or create) -->
      <div v-show="currentStep === 1 && !isEdit" class="wl-wizard-body px-4 py-3">
        <b-form-group label="Clinic patient source" label-class="font-weight-bold">
          <b-button-group class="w-100">
            <b-button
              :variant="patientMode === 'search' ? 'primary' : 'outline-primary'"
              @click="patientMode = 'search'">
              <i class="fas fa-search mr-1"></i> Search {{ contextLabel }} patient
            </b-button>
            <b-button
              :variant="patientMode === 'create' ? 'primary' : 'outline-primary'"
              @click="patientMode = 'create'">
              <i class="fas fa-hospital-user mr-1"></i> Create {{ contextLabel }} patient
            </b-button>
          </b-button-group>
        </b-form-group>

        <div v-if="patientMode === 'search'" class="mt-3">
          <ClinicPatientSearchPanel
            :program-context="programContext"
            :loading="searchLoading"
            @search="runClinicSearch"
            @clear="onSearchClear" />

          <div v-if="searchLoading" class="text-center py-4 text-muted">
            <SpinnerLoading />
            <div class="mt-2">Searching clinic patients...</div>
          </div>
          <div v-else-if="searchRan && !searchResults.length" class="text-center py-4 text-muted">
            No patients found with those filters.
          </div>
          <div v-else-if="searchResults.length" class="wl-search-results mt-3">
            <div
              v-for="patient in searchResults"
              :key="patient.id"
              class="wl-search-result-card"
              :class="{ selected: selectedSystemPatient && selectedSystemPatient.id === patient.id }"
              @click="selectClinicPatient(patient)">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="font-weight-bold">{{ clinicPatientName(patient) }}</div>
                  <small class="text-muted d-block">Clinic ID: {{ patient.id }}</small>
                  <small v-if="patient.email" class="text-muted d-block">{{ patient.email }}</small>
                  <small v-if="patient.phone" class="text-muted d-block">{{ patient.phone }}</small>
                </div>
                <b-badge :variant="contextBadgeVariant" pill>{{ contextLabel }}</b-badge>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="mt-3">
          <p class="text-muted mb-3">
            Register a new <strong>{{ contextLabel }}</strong> clinic patient here. On save we use
            the returned ID to continue with GLP-1 enrollment.
          </p>
          <ClinicPatientCreatePanel
            :program-context="programContext"
            @created="onClinicPatientCreatedInline"
            @error="msg => $emit('error', msg)" />
          <b-alert v-if="form.user_id" show variant="success" class="mt-3 mb-0">
            <strong>Clinic patient ready:</strong>
            {{ form.first_name }} {{ form.last_name }} (#{{ form.user_id }})
          </b-alert>
        </div>

        <b-alert
          v-if="form.user_id && patientMode === 'search'"
          show
          variant="success"
          class="mt-3 mb-0">
          <strong>Selected:</strong> {{ form.first_name }} {{ form.last_name }}
          · Clinic ID {{ form.user_id }}
        </b-alert>
      </div>

      <!-- Step 2: Weightloss program -->
      <div v-show="currentStep === 2 || isEdit" class="wl-wizard-body px-4 py-3">
        <b-alert v-if="form.user_id && !isEdit" show variant="info" class="mb-3">
          Enrolling clinic patient <strong>#{{ form.user_id }}</strong>
          ({{ form.first_name }} {{ form.last_name }}) in the GLP-1 program.
        </b-alert>
        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="Treatment *" label-class="font-weight-bold">
              <treeselect
                v-model="form.weightloss_treatment_id"
                :options="treatmentOptions"
                :clearable="false"
                placeholder="Select treatment" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Facility *" label-class="font-weight-bold">
              <treeselect
                v-model="form.facility_id"
                :options="facilityOptions"
                :normalizer="normalizer"
                :clearable="false"
                placeholder="Select facility"
                :disabled="facilityOptionsLoading" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Status" label-class="font-weight-bold">
              <b-form-select v-model="form.status" :options="statusOptions">
                <template #first>
                  <b-form-select-option :value="null">Active (default)</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Initial weight (kg)" label-class="font-weight-bold">
              <b-form-input v-model="form.initial_weight" type="number" step="0.1" min="0" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Goal weight (kg)" label-class="font-weight-bold">
              <b-form-input v-model="form.goal_weight" type="number" step="0.1" min="0" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Enrolled date" label-class="font-weight-bold">
              <b-form-input v-model="form.enrolled_at" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Notes" label-class="font-weight-bold">
              <b-form-textarea v-model="form.notes" rows="3" placeholder="Optional" />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between align-items-center flex-wrap">
          <b-button
            v-if="currentStep > 1 && !isEdit"
            variant="outline-secondary"
            :disabled="saving"
            @click="prevStep">
            <i class="fas fa-arrow-left mr-1"></i> Back
          </b-button>
          <span v-else></span>
          <div class="d-flex">
            <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="closeModal">
              Cancel
            </b-button>
            <b-button v-if="currentStep === 1 && !isEdit" variant="primary" @click="nextStep">
              Next <i class="fas fa-arrow-right ml-1"></i>
            </b-button>
            <b-button v-else variant="primary" :disabled="saving" @click="submit">
              <SpinnerLoading v-if="saving" />
              <span v-else>
                <i class="fas fa-check mr-1"></i>
                {{ isEdit ? 'Save changes' : 'Enroll in program' }}
              </span>
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

  </div>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BButton,
  BButtonGroup,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import SpinnerLoading from '@/components/SpinnerLoading'
import ClinicPatientSearchPanel from './ClinicPatientSearchPanel.vue'
import ClinicPatientCreatePanel from './ClinicPatientCreatePanel.vue'
import { resolveNumericFacilityId } from '@/core/services/api/weightloss-inventory/facility'
import axiosF from '@/core/services/api/admin/facilities'
import { getWlPatientsApi, userHasInsurance } from '@/core/services/api/weightloss-inventory/laneApi'
import {
  isInsuranceContext,
  searchClinicPatients,
  mapClinicPatientToWlForm,
} from '../clinicPatientBridge'

function defaultProgramForm() {
  const today = new Date().toISOString().slice(0, 10)
  const defaultFacility = resolveNumericFacilityId()
  return {
    user_id: null,
    first_name: '',
    last_name: '',
    weightloss_treatment_id: null,
    facility_id: defaultFacility ? Number(defaultFacility) : null,
    initial_weight: '',
    goal_weight: '',
    enrolled_at: today,
    status: null,
    notes: '',
  }
}

export default {
  name: 'EnrollWlPatientModal',
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BButton,
    BButtonGroup,
    BAlert,
    BBadge,
    Treeselect,
    SpinnerLoading,
    ClinicPatientSearchPanel,
    ClinicPatientCreatePanel,
  },
  props: {
    visible: { type: Boolean, default: false },
    editItem: { type: Object, default: null },
    treatmentOptions: { type: Array, default: () => [] },
    programContext: { type: String, default: 'normal' },
  },
  data() {
    return {
      currentStep: 1,
      patientMode: 'search',
      searchResults: [],
      searchLoading: false,
      searchRan: false,
      selectedSystemPatient: null,
      saving: false,
      facilityOptions: [],
      facilityOptionsLoading: false,
      form: defaultProgramForm(),
      statusOptions: [
        { value: 'active', text: 'Active' },
        { value: 'paused', text: 'Paused' },
        { value: 'discharged', text: 'Discharged' },
      ],
      steps: [
        { key: 'clinic', label: 'Clinic patient' },
        { key: 'program', label: 'GLP-1 program' },
      ],
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
    }
  },
  computed: {
    isEdit() {
      return !!this.editItem?.id
    },
    isInsuranceContext() {
      return isInsuranceContext(this.programContext)
    },
    contextLabel() {
      return this.isInsuranceContext ? 'insurance' : 'regular'
    },
    contextBadgeVariant() {
      return this.isInsuranceContext ? 'warning' : 'primary'
    },
    modalTitle() {
      return this.isEdit ? 'Edit program enrollment' : 'Enroll patient in GLP-1 program'
    },
    modalSubtitle() {
      if (this.isEdit) return 'Update treatment and program data'
      return `Step 1: find or create a ${this.contextLabel} clinic patient · Step 2: assign treatment`
    },
  },
  watch: {
    visible(val) {
      if (val) this.onOpen()
    },
  },
  methods: {
    onOpen() {
      this.loadFacilities()
      if (this.isEdit) {
        this.currentStep = 2
        this.populateFromEdit(this.editItem)
      } else {
        this.resetWizard()
      }
    },
    onHidden() {
      this.resetWizard()
      this.$emit('close')
    },
    resetWizard() {
      this.currentStep = 1
      this.patientMode = 'search'
      this.searchResults = []
      this.searchRan = false
      this.selectedSystemPatient = null
      this.form = defaultProgramForm()
    },
    populateFromEdit(item) {
      this.form = {
        user_id: item.user_id || null,
        first_name: item.first_name || '',
        last_name: item.last_name || '',
        weightloss_treatment_id: item.weightloss_treatment_id,
        facility_id: item.facility_id != null ? Number(item.facility_id) : null,
        initial_weight: item.initial_weight ?? '',
        goal_weight: item.goal_weight ?? '',
        enrolled_at: item.enrolled_at ? String(item.enrolled_at).slice(0, 10) : defaultProgramForm().enrolled_at,
        status: item.status || null,
        notes: item.notes || '',
      }
    },
    async loadFacilities() {
      if (this.facilityOptions.length) return
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({ id: item.id, name: item.name }))
      } catch {
        this.facilityOptions = []
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    clinicPatientName(patient) {
      return `${patient.name || patient.first_name || ''} ${patient.lastname || patient.last_name || ''}`.trim()
    },
    applyClinicPatient(patient) {
      const mapped = mapClinicPatientToWlForm(patient)
      if (!mapped) return
      this.selectedSystemPatient = patient
      Object.assign(this.form, mapped)
    },
    selectClinicPatient(patient) {
      if (this.isInsuranceContext && !userHasInsurance(patient)) {
        this.$emit(
          'error',
          'Insurance enrollment requires a clinic patient with has_insurance = "true"'
        )
        return
      }
      this.applyClinicPatient(patient)
    },
    onSearchClear() {
      this.searchResults = []
      this.searchRan = false
      this.selectedSystemPatient = null
      this.form.user_id = null
    },
    async runClinicSearch(filters) {
      this.searchLoading = true
      this.searchRan = true
      try {
        const res = await searchClinicPatients(this.programContext, 15, filters, 1)
        let items = res.items || []
        if (this.isInsuranceContext) {
          items = items.filter(p => userHasInsurance(p))
        }
        this.searchResults = items
      } catch {
        this.searchResults = []
        this.$emit('error', 'Could not search clinic patients')
      } finally {
        this.searchLoading = false
      }
    },
    onClinicPatientCreatedInline({ patientId, patient, message }) {
      const id = patientId || patient?.id
      if (!id) {
        this.$emit('error', 'Patient created but ID was not returned. Check browser console.')
        return
      }
      const merged = { ...patient, id }
      if (this.isInsuranceContext && !userHasInsurance(merged)) {
        // Paciente insurance recién creado debería traer has_insurance; si no, avisar
        this.$emit(
          'error',
          'Created patient must have insurance (has_insurance="true") to appear in this module'
        )
      }
      this.applyClinicPatient(merged)
      this.$emit('info', message || 'Clinic patient created')
    },
    validateStep1() {
      if (!this.form.user_id) {
        this.$emit(
          'error',
          this.patientMode === 'create'
            ? 'Create the clinic patient using the form above'
            : 'Select a clinic patient from search results'
        )
        return false
      }
      if (!this.form.first_name?.trim() || !this.form.last_name?.trim()) {
        this.$emit('error', 'Clinic patient name is required')
        return false
      }
      if (
        this.isInsuranceContext &&
        this.selectedSystemPatient &&
        !userHasInsurance(this.selectedSystemPatient)
      ) {
        this.$emit(
          'error',
          'Insurance enrollment requires a clinic patient with has_insurance = "true"'
        )
        return false
      }
      return true
    },
    validateStep2() {
      if (!this.form.weightloss_treatment_id) {
        this.$emit('error', 'Treatment is required')
        return false
      }
      if (this.form.facility_id == null || this.form.facility_id === '') {
        this.$emit('error', 'Facility is required')
        return false
      }
      return true
    },
    nextStep() {
      if (!this.validateStep1()) return
      this.currentStep = 2
    },
    prevStep() {
      this.currentStep = 1
    },
    closeModal() {
      this.$emit('update:visible', false)
    },
    buildPayload() {
      const payload = {
        first_name: String(this.form.first_name).trim(),
        last_name: String(this.form.last_name).trim(),
        weightloss_treatment_id: Number(this.form.weightloss_treatment_id),
        facility_id: Number(this.form.facility_id),
        user_id: Number(this.form.user_id),
      }
      if (this.form.initial_weight !== '' && this.form.initial_weight != null) {
        payload.initial_weight = Number(this.form.initial_weight)
      }
      if (this.form.goal_weight !== '' && this.form.goal_weight != null) {
        payload.goal_weight = Number(this.form.goal_weight)
      }
      if (this.form.enrolled_at) payload.enrolled_at = this.form.enrolled_at
      if (this.form.status) payload.status = this.form.status
      if (this.form.notes?.trim()) payload.notes = this.form.notes.trim()
      return payload
    },
    async submit() {
      if (!this.isEdit && !this.validateStep1()) return
      if (!this.validateStep2()) return
      this.saving = true
      try {
        const payload = this.buildPayload()
        const patientsApi = getWlPatientsApi(this.programContext)
        if (this.isEdit) {
          await patientsApi.update(this.editItem.id, payload)
          this.$emit('saved', { mode: 'update' })
        } else {
          await patientsApi.create(payload)
          this.$emit('saved', { mode: 'create' })
        }
        this.closeModal()
      } catch (e) {
        this.$emit('error', e.message || 'Could not save enrollment')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.wl-wizard-step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #eef0f8;
  color: #7e8299;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
}

.wl-wizard-step.active .wl-wizard-step-circle {
  background: #3699ff;
  color: #fff;
}

.wl-wizard-step.done .wl-wizard-step-circle {
  background: #1bc5bd;
  color: #fff;
}

.wl-search-results {
  max-height: 260px;
  overflow-y: auto;
}

.wl-search-result-card {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.wl-search-result-card:hover,
.wl-search-result-card.selected {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.15);
  background: #f8fbff;
}

::v-deep .wl-enroll-patient-modal .modal-content {
  border: none;
  border-radius: 12px;
}

::v-deep .wl-enroll-patient-modal .modal-header,
::v-deep .wl-enroll-patient-modal .modal-footer {
  border-color: #eff2f5;
  padding: 1rem 1.5rem;
}

::v-deep .wl-wizard-body .vue-treeselect__control,
::v-deep .wl-wizard-body .form-control,
::v-deep .wl-wizard-body .custom-select {
  min-height: 38px;
}
</style>
